import { LlmOutputFailureTypesEnum } from '@eluve/graphql-types';
import type { MessageKey } from '@eluve/i18n';

export type SummarizationErrorMessage = {
  titleKey: MessageKey;
  descriptionKey: MessageKey;
  showRegenerateHint: boolean;
  showTemplateSelector?: boolean;
  variant: 'error' | 'warning';
};

const COMMON_ERROR_TITLE_KEYS = {
  SOMETHING_WRONG: 'appointment.summarizationError.somethingWrong',
  FAILED_TO_GENERATE: 'appointment.summarizationError.failedToGenerate',
} as const satisfies Record<string, MessageKey>;

const COMMON_ERROR_DESCRIPTION_KEYS = {
  TEAM_INVESTIGATING: 'appointment.summarizationError.teamInvestigating',
  RECORDING_TOO_SHORT: 'appointment.summarizationError.recordingTooShort',
  TEMPLATE_SELECTION_REQUIRED:
    'appointment.summarizationError.templateSelectionRequired',
} as const satisfies Record<string, MessageKey>;

export const SUMMARIZATION_ERROR_MESSAGES: Record<
  LlmOutputFailureTypesEnum,
  SummarizationErrorMessage
> = {
  TEMPLATE_MISSING: {
    variant: 'warning',
    titleKey: COMMON_ERROR_TITLE_KEYS.FAILED_TO_GENERATE,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.TEMPLATE_SELECTION_REQUIRED,
    showRegenerateHint: true,
    showTemplateSelector: true,
  },
  DEGRADED_TRANSCRIPT: {
    variant: 'error',
    titleKey: COMMON_ERROR_TITLE_KEYS.SOMETHING_WRONG,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.TEAM_INVESTIGATING,
    showRegenerateHint: false,
  },
  NO_MEDICAL_CONTEXT: {
    variant: 'warning',
    titleKey: COMMON_ERROR_TITLE_KEYS.FAILED_TO_GENERATE,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.RECORDING_TOO_SHORT,
    showRegenerateHint: false,
  },
  TRANSCRIPT_TOO_SHORT: {
    variant: 'warning',
    titleKey: COMMON_ERROR_TITLE_KEYS.FAILED_TO_GENERATE,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.RECORDING_TOO_SHORT,
    showRegenerateHint: false,
  },
  LLM_GENERATION_ERROR: {
    variant: 'error',
    titleKey: COMMON_ERROR_TITLE_KEYS.SOMETHING_WRONG,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.TEAM_INVESTIGATING,
    showRegenerateHint: true,
  },
  SCHEMA_VALIDATION_ERROR: {
    variant: 'warning',
    titleKey: COMMON_ERROR_TITLE_KEYS.FAILED_TO_GENERATE,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.TEMPLATE_SELECTION_REQUIRED,
    showRegenerateHint: true,
    showTemplateSelector: true,
  },
  UNKNOWN: {
    variant: 'error',
    titleKey: COMMON_ERROR_TITLE_KEYS.SOMETHING_WRONG,
    descriptionKey: COMMON_ERROR_DESCRIPTION_KEYS.TEAM_INVESTIGATING,
    showRegenerateHint: true,
  },
};

export const getSummarizationErrorMessage = (
  failureType: LlmOutputFailureTypesEnum | null | undefined,
): SummarizationErrorMessage => {
  if (!failureType) {
    return SUMMARIZATION_ERROR_MESSAGES.UNKNOWN;
  }

  return (
    SUMMARIZATION_ERROR_MESSAGES[failureType] ??
    SUMMARIZATION_ERROR_MESSAGES.UNKNOWN
  );
};
